import type { NextApiRequest, NextApiResponse } from 'next'
import {
  Currency,
  type Language,
  type OrderAddressDTO,
  OrderStatus,
  type UpdateInvoiceOrderDTO
} from 'ecosystem'
import { useLocaleCredentials as setLocaleCredentials } from 'api'
import storefrontApi from 'api/storefront-api'
import {
  buyerVatRatesFor,
  createLogger,
  DEFAULT_COUNTRY,
  getVatRateByCountryCode,
  shipmentFromOrderRoot
} from 'shared-utils'
import {
  generateIngridShippingProviderResult,
  generateNshiftShippingProviderResult,
  generatePostnordShippingProviderResult
} from 'api/storefront-api/ordersEndpoint/provisionOrder'
import {
  computeOrderTotalsFromGross,
  transformOrderItemsToCreateOrderItems
} from 'api/utils/orders'
import { handlerWithLogging } from '../../../logging'
import {
  buildShipmentFromDefault,
  buildShipmentFromIngrid,
  buildShipmentFromNshift,
  buildShipmentFromPostnord,
  type BuiltShipment,
  withShipment
} from '../shippingProviders'
import { type InvoiceOrderHandlerOpts } from './types'

const logger = createLogger('submitInvoiceOrderHandler')

interface SubmitInvoiceOrderRequest {
  orderNumber: string
}

const customerBillingAddress = (
  customer: Awaited<ReturnType<typeof storefrontApi.customer.findById>>
): OrderAddressDTO | undefined => {
  const main = customer.addresses?.find((a) => a.main) ?? customer.addresses?.[0]
  if (!main) return undefined
  return {
    address1: main.address1 ?? '',
    address2: main.address2 ?? '',
    attention: main.attention ?? [customer.firstName, customer.lastName].filter(Boolean).join(' '),
    country: main.country ?? '',
    mobilePhone: main.mobilePhone ?? '',
    postalCode: main.postalCode ?? '',
    postalRegion: main.postalRegion ?? '',
    reference: main.reference ?? ''
  }
}

/**
 * POST /api/orders/invoice-submit — submits an existing DRAFT invoice order.
 *
 * Server-side provisioning flow, mirroring the upfront `provisionOrder` family:
 * 1. Fetch the DRAFT invoice order by `orderNumber`.
 * 2. Fetch the Customer record (provides contact info for carrier bookings
 *    and a billing-address fallback when the DRAFT doesn't carry one).
 * 3. Branch by `Order.shippingProvider`:
 *    - INGRID: server-side `getSession` + `completeSession`, build canonical
 *      shipment from the resulting session.
 *    - POSTNORD: read persisted `shippingInfo.additionalInfo.postnordDetails`
 *      from the DRAFT, book the shipment via Postnord, build canonical
 *      shipment from the booking response.
 *    - NSHIFT: same — read persisted `nshiftDetails`, book via Nshift, build
 *      canonical shipment.
 *    - DEFAULT: no carrier work, just provider-only shipment + status flip.
 * 4. Force `status = CREATED` and PUT to `/storefront/invoice` — that's the
 *    BE's publish/dispatch moment.
 *
 * Per FEATURE_B2B_ORDER_PLACEMENT.md §7 the BE rejects root-level
 * `shippingProvider` / `shippingId` / `shippingInfo` / `shippingAddress` on
 * the request DTO. All shipping data lives on `shipments[0]`.
 *
 * `customerId` is overwritten from the iron-session so the FE can't spoof it.
 */
export const submitInvoiceOrderHandler = handlerWithLogging(
  async (req: NextApiRequest, res: NextApiResponse, opts: InvoiceOrderHandlerOpts) => {
    if (req.method !== 'POST') {
      res.status(405).json({ error: 'Method not supported.' })
      return
    }
    if (!opts.customerId) {
      res.status(401).json({ error: 'Unauthorized — invoice flow requires a signed-in customer.' })
      return
    }

    const locale = (req.cookies.NEXT_LOCALE as Language) || 'sv'
    setLocaleCredentials(locale)

    try {
      const { orderNumber }: SubmitInvoiceOrderRequest = JSON.parse(req.body)
      if (!orderNumber) {
        res.status(400).json({ error: 'orderNumber is required to submit an invoice order.' })
        return
      }

      // 1. Fetch the DRAFT order — items + customer are required since the
      // PUT replaces (does not merge) `items`; omitting them would erase the
      // persisted lines.
      const { data: orderData, error: getOrderError } =
        await storefrontApi.invoiceOrders.getInvoiceOrder({
          orderNumber,
          includeItems: true,
          includeCustomer: true
        })

      logger.log('getOrder response', orderData)

      if (getOrderError || !orderData) {
        res.status(404).json({
          error: typeof getOrderError === 'string' ? getOrderError : 'invoice-order-not-found'
        })
        return
      }

      // 2. Fetch the Customer (contact info for carrier bookings + billing fallback)
      const customer = await storefrontApi.customer.findById(opts.customerId)
      const contactName = [customer.firstName, customer.lastName].filter(Boolean).join(' ')
      const contactPhone = customer.phones?.[0]?.number ?? ''
      const billingAddress = orderData.billingAddress ?? customerBillingAddress(customer)

      // 3. Build a canonical shipment for the current carrier.
      let built: BuiltShipment | undefined

      switch (orderData.shippingProvider) {
        case 'INGRID': {
          const result = await generateIngridShippingProviderResult(orderData)
          built = buildShipmentFromIngrid(result?.ingridResponse)
          break
        }
        case 'POSTNORD': {
          const result = await generatePostnordShippingProviderResult(
            orderData,
            {
              phoneNo: contactPhone,
              smsNo: contactPhone,
              emailAddress: customer.email,
              contactName
            },
            opts.shippingProviderConfig
          )
          built = buildShipmentFromPostnord(result?.postnordResponse)
          break
        }
        case 'NSHIFT': {
          const result = await generateNshiftShippingProviderResult(orderData, {
            name: contactName,
            email: customer.email,
            phone: contactPhone
          })
          built = buildShipmentFromNshift(result?.nshiftResponse)
          break
        }
        default: {
          // DEFAULT or undefined → no carrier booking server-side. The FE
          // already pushed a full `shipments[0]` (delivery address or pickup
          // details) during the draft-sync phase, which the BE reflects back
          // at the root level on GET. Rebuild the shipment from those root
          // fields so the PUT round-trips them. Fall back to a provider-only
          // stub when no shipping data is persisted yet.
          const fromRoot = shipmentFromOrderRoot(orderData)
          if (fromRoot) {
            built = { shipment: fromRoot }
          } else if (orderData.shippingProvider) {
            built = buildShipmentFromDefault({
              provider: orderData.shippingProvider,
              shipmentId: orderData.shippingId
            })
          } else {
            built = undefined
          }
          break
        }
      }

      // Round-trip every field the PUT can replace — items, totals,
      // discount, shipping cost — so the BE doesn't erase persisted state.
      // Recompute totals from the persisted gross items + shippingCost so
      // `orderAmount` is the freight-inclusive order total and `orderTaxAmount`
      // the VAT inside it, goods and freight together. Both were set at create
      // time from items only, before the carrier widget populated shippingCost.
      // This is the one place the final figures are formed — the same shape the
      // upfront flow persists, so nothing downstream branches on `paymentType`.
      //
      // The shipping RATE is resolved onto the buyer's VAT treatment: a
      // zero-rated buyer owes no VAT on freight either, so the store's own rate
      // would overstate `orderTaxAmount`. The shipping COST is taken as
      // persisted — the carrier widget already re-based the quote onto this
      // buyer before syncing the draft, so converting again here would net it
      // twice.
      const persistedItems = orderData.items
        ? transformOrderItemsToCreateOrderItems(orderData.items)
        : []
      const buyerVat = buyerVatRatesFor(
        customer?.companyProfile,
        opts.invoicePaymentConfig?.countryCode ?? DEFAULT_COUNTRY
      )
      const shippingCost = orderData.shippingCost ?? 0
      const shippingVatRate = buyerVat.resolveRate(
        getVatRateByCountryCode(opts.invoicePaymentConfig?.countryCode)
      )
      const { orderAmount, orderTaxAmount, orderDiscount } = computeOrderTotalsFromGross(
        persistedItems,
        shippingCost,
        shippingVatRate
      )

      const basePayload: UpdateInvoiceOrderDTO = {
        orderNumber: orderData.orderNumber ?? orderNumber,
        storeId: orderData.storeId,
        customerId: opts.customerId,
        status: OrderStatus.CREATED,
        currency: orderData.currency ?? Currency.SEK,
        items: persistedItems,
        orderAmount,
        orderTaxAmount,
        ...(orderDiscount > 0 ? { orderDiscount } : {}),
        discountCode: orderData.discountCode,
        // The buyer-resolved cost, not the raw carrier quote — it has to agree
        // with the `orderTaxAmount` computed from it just above.
        shippingCost,
        ...(billingAddress ? { billingAddress } : {})
      }

      const payload = withShipment(basePayload, built) as UpdateInvoiceOrderDTO
      payload.status = OrderStatus.CREATED

      logger.log('BE REQ PAYLOAD', payload)

      const updated = await storefrontApi.invoiceOrders.updateInvoiceOrder(payload)
      res.status(200).json(updated)
    } catch (e) {
      res.status(400).json({ error: e instanceof Error ? e.message : 'Unknown error' })
    }
  }
)
